import React from 'react';
import { Box, IconButton, Typography } from '@mui/material';
import { Add, Close, KeyboardArrowUp } from '@mui/icons-material';
import { ChipInput, CreatableSelect, StatusChip } from '../../ui';
import { tokens } from '../../../theme/tokens';
import type { ProductTypeRule } from '../mappingModel';
import CategorySelect from './CategorySelect';
import { LinkAction, StepCircle, v2CardSx } from './v2Ui';

interface NameRulesStepProps {
  /** Number shown in the circle; 2, or 1 when the taxonomy step is hidden. */
  n: number;
  rules: ProductTypeRule[];
  onChange: (rules: ProductTypeRule[]) => void;
  typeOptions: string[];
  /** Flag `name_rule_shopify_category`: a rule may also set the Shopify category. */
  categoryEnabled: boolean;
  taxonomyOptions: Array<{ gid: string; label: string }>;
  /** Flag `structured_mapping_inputs`. */
  keywordSuggestions?: string[];
  matchCounts?: number[] | null;
}

/** Step 2 (optional): keyword in the product name -> type (and optionally a category). First match wins. */
const NameRulesStep: React.FC<NameRulesStepProps> = ({
  n,
  rules,
  onChange,
  typeOptions,
  categoryEnabled,
  taxonomyOptions,
  keywordSuggestions,
  matchCounts,
}) => {
  const update = (index: number, next: ProductTypeRule) => onChange(rules.map((rule, i) => (i === index ? next : rule)));
  return (
    <Box sx={{ ...v2CardSx, px: 2.25, py: 2, display: 'flex', flexDirection: 'column', gap: 1.25 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <StepCircle n={n} tone="info" />
        <Typography sx={{ fontSize: 14, fontWeight: 700 }}>Name Rules</Typography>
        <StatusChip label="Optional" tone="neutral" />
      </Box>
      <Typography sx={{ fontSize: 12.5, color: tokens.ink.muted }}>
        Optional. Use Name Rules when your Store Taxonomy does not classify a product, or when you don&apos;t use a Store Taxonomy and need specific product names to override Motzu&apos;s classification. If your Store Taxonomy already classifies your products correctly, you can leave this section empty.
      </Typography>
      <Typography sx={{ fontSize: 12.5, color: tokens.ink.muted }}>
        {categoryEnabled
          ? 'If the product name contains the keyword, it gets that Shopify Product Type, and the Shopify Product Category if you set one. Top to bottom, first match wins.'
          : 'If the product name contains the keyword, it gets that Shopify Product Type. Name Rules run after Motzu Classification Overrides, so a rule only applies when the product\'s Motzu Classification has no Shopify Product Type there. Top to bottom, first match wins.'}
      </Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        {rules.map((rule, index) => (
          <Box
            key={index}
            sx={{
              display: 'grid',
              alignItems: 'start',
              columnGap: 1,
              rowGap: 0.75,
              gridTemplateColumns: {
                xs: '20px minmax(0,1fr) auto',
                md: categoryEnabled
                  ? '20px minmax(0,1.2fr) minmax(0,1fr) minmax(0,1fr) auto'
                  : '20px minmax(0,1.2fr) minmax(0,1fr) auto',
              },
            }}
          >
            <Typography sx={{ pt: 1, fontSize: 12, fontWeight: 700, color: tokens.ink.faint }}>{index + 1}</Typography>
            <Box sx={{ minWidth: 0, display: 'flex', gap: 0.75, alignItems: 'flex-start' }}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <ChipInput
                  aria-label={`Rule ${index + 1} keyword`}
                  placeholder="Keyword"
                  value={rule.keywords}
                  suggestions={keywordSuggestions}
                  onChange={(keywords) => update(index, { ...rule, keywords })}
                />
              </Box>
              {matchCounts && (
                <Box sx={{ pt: 0.5 }}>
                  <StatusChip label={`matches ${matchCounts[index] ?? 0}`} tone={matchCounts[index] ? 'success' : 'neutral'} />
                </Box>
              )}
            </Box>
            <Box sx={{ gridColumn: { xs: '2 / 3', md: 'auto' }, minWidth: 0 }}>
              <CreatableSelect
                label=""
                aria-label={`Rule ${index + 1} Shopify Product Type`}
                value={rule.productType}
                options={typeOptions}
                onChange={(value) => update(index, { ...rule, productType: value })}
                placeholder="Type"
              />
            </Box>
            {categoryEnabled && (
              <Box sx={{ gridColumn: { xs: '2 / 3', md: 'auto' }, minWidth: 0 }}>
                <CategorySelect
                  aria-label={`Rule ${index + 1} Shopify Product Category`}
                  value={rule.taxonomyCategoryGid || ''}
                  options={taxonomyOptions}
                  emptyLabel="Keep current category"
                  onChange={(gid) => {
                    const { taxonomyCategoryGid: _previous, ...rest } = rule;
                    update(index, gid ? { ...rest, taxonomyCategoryGid: gid } : rest);
                  }}
                  sx={{ height: 40 }}
                />
              </Box>
            )}
            <Box sx={{ display: 'flex', gridColumn: { xs: '3 / 4', md: 'auto' }, gridRow: { xs: '1', md: 'auto' } }}>
              <IconButton
                size="small"
                aria-label="Move rule up"
                disabled={index === 0}
                onClick={() => {
                  const next = [...rules];
                  const [row] = next.splice(index, 1);
                  next.splice(index - 1, 0, row);
                  onChange(next);
                }}
              >
                <KeyboardArrowUp fontSize="small" />
              </IconButton>
              <IconButton size="small" aria-label="Remove rule" onClick={() => onChange(rules.filter((_, i) => i !== index))}>
                <Close sx={{ fontSize: 16 }} />
              </IconButton>
            </Box>
          </Box>
        ))}
      </Box>
      <Box>
        <LinkAction startIcon={<Add sx={{ fontSize: 15 }} />} onClick={() => onChange([...rules, { keywords: [], productType: '' }])}>
          Add rule
        </LinkAction>
      </Box>
    </Box>
  );
};

export default NameRulesStep;
